import { responsiveFontSizes } from '@mui/material';
import { createTheme, alpha } from '@mui/material/styles';
import shadows from './shadows';
import { light, dark, varAlpha } from './palette';
import { typography } from './typography';

const getTheme = (mode, themeToggler) =>
  responsiveFontSizes(
    createTheme({
      palette: mode === 'light' ? light : dark,
      shadows: shadows(mode),
      customShadows: {
        // Elevation shadows (z1-z24)
        z1: mode === 'light'
          ? '0 1px 2px 0 rgba(145, 158, 171, 0.16)'
          : '0 1px 2px 0 rgba(0, 0, 0, 0.24)',
        z4: mode === 'light'
          ? '0 4px 8px 0 rgba(145, 158, 171, 0.16)'
          : '0 4px 8px 0 rgba(0, 0, 0, 0.24)',
        z8: mode === 'light'
          ? '0 8px 16px 0 rgba(145, 158, 171, 0.16)'
          : '0 8px 16px 0 rgba(0, 0, 0, 0.24)',
        z12: mode === 'light'
          ? '0 12px 24px -4px rgba(145, 158, 171, 0.16)'
          : '0 12px 24px -4px rgba(0, 0, 0, 0.24)',
        z16: mode === 'light'
          ? '0 16px 32px -4px rgba(145, 158, 171, 0.16)'
          : '0 16px 32px -4px rgba(0, 0, 0, 0.24)',
        z20: mode === 'light'
          ? '0 20px 40px -4px rgba(145, 158, 171, 0.16)'
          : '0 20px 40px -4px rgba(0, 0, 0, 0.24)',
        z24: mode === 'light'
          ? '0 24px 48px 0 rgba(145, 158, 171, 0.16)'
          : '0 24px 48px 0 rgba(0, 0, 0, 0.24)',

        // Component shadows
        card: mode === 'light'
          ? '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)'
          : '0 0 2px 0 rgba(0, 0, 0, 0.24), 0 12px 24px -4px rgba(0, 0, 0, 0.24)',
        dropdown: mode === 'light'
          ? '0 0 2px 0 rgba(145, 158, 171, 0.24), -20px 20px 40px -4px rgba(145, 158, 171, 0.24)'
          : '0 0 2px 0 rgba(0, 0, 0, 0.24), -20px 20px 40px -4px rgba(0, 0, 0, 0.24)',
        dialog: '-40px 40px 80px -8px rgba(0, 0, 0, 0.24)',

        // Color-specific shadows (for buttons/hover states)
        primary: '0 8px 16px 0 rgba(0, 167, 111, 0.24)',
        secondary: '0 8px 16px 0 rgba(142, 51, 255, 0.24)',
        info: '0 8px 16px 0 rgba(0, 184, 217, 0.24)',
        success: '0 8px 16px 0 rgba(34, 197, 94, 0.24)',
        warning: '0 8px 16px 0 rgba(255, 171, 0, 0.24)',
        error: '0 8px 16px 0 rgba(255, 86, 48, 0.24)',
      },
      typography,
      zIndex: {
        appBar: 1200,
        drawer: 1300,
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              fontWeight: 700,
              borderRadius: 5,
              paddingTop: 10,
              paddingBottom: 10,
            },
            containedSecondary: mode === 'light' ? { color: 'white' } : {},
            // Add hover shadows for contained buttons
            containedPrimary: {
              '&:hover': {
                boxShadow: '0 8px 16px 0 rgba(0, 167, 111, 0.24)',
              },
            },
            containedInfo: {
              '&:hover': {
                boxShadow: '0 8px 16px 0 rgba(0, 184, 217, 0.24)',
              },
            },
            containedSuccess: {
              '&:hover': {
                boxShadow: '0 8px 16px 0 rgba(34, 197, 94, 0.24)',
              },
            },
            containedWarning: {
              '&:hover': {
                boxShadow: '0 8px 16px 0 rgba(255, 171, 0, 0.24)',
              },
            },
            containedError: {
              '&:hover': {
                boxShadow: '0 8px 16px 0 rgba(255, 86, 48, 0.24)',
              },
            },
          },
          // Add "soft" button variant
          variants: [
            {
              props: { variant: 'soft' },
              style: {
                backgroundColor: mode === 'light'
                  ? alpha('#919EAB', 0.08)
                  : alpha('#919EAB', 0.16),
                color: mode === 'light' ? '#454F5B' : '#C4CDD5',
                '&:hover': {
                  backgroundColor: mode === 'light'
                    ? alpha('#919EAB', 0.24)
                    : alpha('#919EAB', 0.32),
                },
              },
            },
            {
              props: { variant: 'soft', color: 'primary' },
              style: {
                color: '#007867',
                backgroundColor: alpha('#00A76F', 0.16),
                '&:hover': {
                  backgroundColor: alpha('#00A76F', 0.32),
                },
              },
            },
            {
              props: { variant: 'soft', color: 'secondary' },
              style: {
                color: '#5119B7',
                backgroundColor: alpha('#8E33FF', 0.16),
                '&:hover': {
                  backgroundColor: alpha('#8E33FF', 0.32),
                },
              },
            },
            {
              props: { variant: 'soft', color: 'info' },
              style: {
                color: '#006C9C',
                backgroundColor: alpha('#00B8D9', 0.16),
                '&:hover': {
                  backgroundColor: alpha('#00B8D9', 0.32),
                },
              },
            },
            {
              props: { variant: 'soft', color: 'success' },
              style: {
                color: '#118D57',
                backgroundColor: alpha('#22C55E', 0.16),
                '&:hover': {
                  backgroundColor: alpha('#22C55E', 0.32),
                },
              },
            },
            {
              props: { variant: 'soft', color: 'warning' },
              style: {
                color: '#B76E00',
                backgroundColor: alpha('#FFAB00', 0.16),
                '&:hover': {
                  backgroundColor: alpha('#FFAB00', 0.32),
                },
              },
            },
            {
              props: { variant: 'soft', color: 'error' },
              style: {
                color: '#B71D18',
                backgroundColor: alpha('#FF5630', 0.16),
                '&:hover': {
                  backgroundColor: alpha('#FF5630', 0.32),
                },
              },
            },
          ],
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              borderRadius: 5,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 5,
              // Improve focus state
              '&.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#212B36' : '#FFFFFF',
                  borderWidth: '2px',
                },
              },
            },
            input: {
              borderRadius: 5,
            },
            notchedOutline: {
              borderColor: mode === 'light'
                ? alpha('#919EAB', 0.2)
                : alpha('#919EAB', 0.24),
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 8,
              position: 'relative',
              zIndex: 0, // Fix Safari overflow with border radius
            },
          },
        },
        MuiCardContent: {
          styleOverrides: {
            root: {
              padding: 24, // theme.spacing(3)
            },
          },
        },
        MuiCardHeader: {
          defaultProps: {
            titleTypographyProps: { variant: 'h6' },
            subheaderTypographyProps: {
              variant: 'body2',
              marginTop: '4px',
            },
          },
          styleOverrides: {
            root: {
              padding: '24px 24px 0', // theme.spacing(3, 3, 0)
            },
          },
        },
        MuiPaper: {
          defaultProps: {
            elevation: 0, // No elevation by default
          },
          styleOverrides: {
            root: {
              backgroundImage: 'none', // Remove default MUI gradient
            },
            outlined: {
              borderColor: mode === 'light'
                ? alpha('#919EAB', 0.16)
                : alpha('#919EAB', 0.24),
            },
          },
        },
      },
      themeToggler,
    }),
  );

export default getTheme;
